import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { motion } from "framer-motion";
import { X, Heart, Loader2 } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { detectCurrency, CURRENCY_SYMBOL, formatMoney } from "@/lib/currency";

const CURRENCIES = [
  { code: "gbp", label: "GBP £" },
  { code: "usd", label: "USD $" },
  { code: "eur", label: "EUR €" },
];

export default function DonateModal({ onClose }) {
  const [currency, setCurrency] = useState(detectCurrency());
  const [tiers, setTiers] = useState({ tier_small: 300, tier_medium: 500, tier_large: 1000 });
  const [selectedTier, setSelectedTier] = useState("tier_medium");
  const [customAmount, setCustomAmount] = useState(""); // in whole currency units
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/payments/config").then((r) => {
      if (r.data.fixed_donation_amounts) setTiers(r.data.fixed_donation_amounts);
    });
  }, []);

  const currentCents = customAmount
    ? Math.max(100, Math.round(parseFloat(customAmount) * 100))
    : tiers[selectedTier];

  const donate = async () => {
    if (!currentCents || currentCents < 100) return toast.error("Minimum donation is 1");
    if (currentCents > 100000) return toast.error("Maximum donation is 1000");
    setBusy(true);
    try {
      const { data } = await api.post("/payments/donate", {
        amount_cents: currentCents,
        currency,
        origin_url: window.location.origin,
      });
      window.location.assign(data.checkout_url);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't start checkout");
      setBusy(false);
    }
  };

  return createPortal(
    <motion.div
      className="fixed inset-0 z-[55] bg-kawaii-text/40 backdrop-blur-md flex items-center justify-center p-4 overflow-y-auto"
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      onClick={onClose}
      data-testid="donate-modal"
    >
      <motion.div
        onClick={(e) => e.stopPropagation()}
        initial={{ scale: 0.94, y: 10 }} animate={{ scale: 1, y: 0 }}
        className="kawaii-card max-w-md w-full my-6 p-6 md:p-8 relative"
      >
        <button
          onClick={onClose}
          data-testid="close-donate-btn"
          className="kawaii-btn absolute top-4 right-4 w-9 h-9 rounded-full bg-white border border-kawaii-border flex items-center justify-center"
        >
          <X className="w-4 h-4" strokeWidth={2.5} />
        </button>

        <div className="flex items-center gap-3 mb-2">
          <div className="w-11 h-11 rounded-2xl bg-kawaii-pink/25 border border-kawaii-pink/50 flex items-center justify-center">
            <Heart className="w-5 h-5 text-kawaii-pink" strokeWidth={2.5} fill="#F4A8CD" />
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-[0.28em] text-kawaii-textSoft font-bold">Support the app</p>
            <h2 className="font-heading text-2xl text-kawaii-text">A little goes a long way ♡</h2>
          </div>
        </div>

        <p className="text-sm font-body text-kawaii-textSoft mt-3 leading-relaxed">
          Donations help our creator Jen (a fellow Lymphie) save up for LVA surgery that she has to self-fund.{" "}
          <Link to="/about" onClick={onClose} data-testid="donate-modal-about-link" className="underline underline-offset-2 text-kawaii-text">
            Read Jen's story
          </Link>.
        </p>

        {/* Fixed tiers */}
        <div className="mt-5 grid grid-cols-3 gap-2">
          {Object.entries(tiers).map(([key, cents]) => {
            const active = !customAmount && selectedTier === key;
            return (
              <button
                key={key}
                onClick={() => { setSelectedTier(key); setCustomAmount(""); }}
                data-testid={`donate-${key}`}
                className={`kawaii-btn p-3 rounded-2xl text-center border ${
                  active ? 'bg-kawaii-pink/25 border-kawaii-pink ring-2 ring-kawaii-pink/40' : 'bg-white border-kawaii-border'
                }`}
              >
                <div className="font-heading text-lg text-kawaii-text">{formatMoney(cents, currency)}</div>
                <div className="text-[10px] font-body text-kawaii-textSoft uppercase tracking-wider mt-0.5">
                  {key === "tier_small" ? "Sprinkle" : key === "tier_medium" ? "Bloom" : "Bouquet"}
                </div>
              </button>
            );
          })}
        </div>

        {/* Custom amount */}
        <div className="mt-4">
          <label className="text-xs uppercase tracking-[0.2em] text-kawaii-textSoft font-bold">Or pick your own</label>
          <div className="mt-2 flex items-center gap-2 px-3 py-2 rounded-2xl border border-kawaii-border bg-white focus-within:ring-2 focus-within:ring-kawaii-pink/40">
            <span className="text-lg text-kawaii-text font-heading">{CURRENCY_SYMBOL[currency]}</span>
            <input
              type="number"
              min="1"
              step="0.5"
              placeholder="Pay what you like"
              value={customAmount}
              onChange={(e) => setCustomAmount(e.target.value)}
              data-testid="custom-amount"
              className="flex-1 bg-transparent text-kawaii-text font-body outline-none"
            />
          </div>
        </div>

        {/* Currency picker */}
        <div className="mt-4 flex items-center justify-end gap-1.5 rounded-full">
          {CURRENCIES.map((c) => (
            <button
              key={c.code}
              onClick={() => setCurrency(c.code)}
              data-testid={`donate-currency-${c.code}`}
              className={`kawaii-btn text-[11px] px-2.5 py-1 rounded-full font-body font-semibold border ${
                currency === c.code ? 'bg-kawaii-pink/25 border-kawaii-pink text-kawaii-text' : 'bg-white border-kawaii-border text-kawaii-textSoft'
              }`}
            >{c.label}</button>
          ))}
        </div>

        <button
          onClick={donate}
          disabled={busy}
          data-testid="donate-checkout-btn"
          className="kawaii-btn mt-6 w-full py-3 rounded-full bg-gradient-to-r from-kawaii-pink to-kawaii-peach text-white font-heading font-semibold text-sm shadow-md inline-flex items-center justify-center gap-2 disabled:opacity-60"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Heart className="w-4 h-4" strokeWidth={2.5} fill="currentColor" />}
          Donate {formatMoney(currentCents, currency)}
        </button>

        <p className="text-[10px] text-kawaii-textSoft font-body italic text-center mt-3">
          Secure checkout by Stripe. Donations are non-refundable.
        </p>
      </motion.div>
    </motion.div>,
    document.body
  );
}
